import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

/**
 * Fixed animated backdrop in the mark's colours: blue, emerald and amber
 * fields drifting over the page ground, plus a masked grid. Entirely
 * decorative, composited on the GPU (transform only) so scrolling stays fast,
 * and tinted through theme variables so it works in light and dark.
 */
export function Aurora() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="bg-bg absolute inset-0" />

      <div className="animate-drift-a absolute -top-[22%] -left-[12%] size-[55vw] rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--blue-vivid)_36%,transparent),transparent_65%)] blur-[90px]" />
      <div className="animate-drift-b absolute top-[26%] -right-[16%] size-[52vw] rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--emerald-vivid)_30%,transparent),transparent_65%)] blur-[100px]" />
      <div className="animate-drift-a absolute bottom-[-18%] left-[22%] size-[46vw] rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--amber-vivid)_24%,transparent),transparent_65%)] blur-[110px] [animation-delay:-8s]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--grid-line)_1px,transparent_1px),linear-gradient(to_bottom,var(--grid-line)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_50%_0%,#000_20%,transparent_75%)]" />
    </div>
  );
}

/** Thin gradient bar showing read progress. */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      if (barRef.current) barRef.current.style.transform = `scaleX(${ratio})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div aria-hidden="true" className="fixed inset-x-0 top-0 z-[60] h-0.5 bg-transparent">
      <div ref={barRef} className="bg-brand h-full origin-left scale-x-0 will-change-transform" />
    </div>
  );
}

/**
 * Soft glow that trails the pointer. Desktop pointers only — never mounted for
 * touch, coarse pointers or reduced-motion users, so it costs them nothing.
 */
export function CursorGlow() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return;
    // Recomputed whenever the motion preference changes, so turning reduced
    // motion on mid-session unmounts the glow instead of latching it on.
    setEnabled(!reduced && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }, [reduced]);

  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    if (!node) return;

    let frame = 0;
    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let currentX = x;
    let currentY = y;

    const render = () => {
      // Lerp toward the pointer so the glow trails rather than snaps.
      currentX += (x - currentX) * 0.12;
      currentY += (y - currentY) * 0.12;
      node.style.transform = `translate3d(${currentX - 300}px, ${currentY - 300}px, 0)`;
      frame = requestAnimationFrame(render);
    };
    const onMove = (event: PointerEvent) => {
      x = event.clientX;
      y = event.clientY;
    };

    frame = requestAnimationFrame(render);
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid="cursor-glow"
      className="pointer-events-none fixed top-0 left-0 -z-10 size-[600px] rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--blue-vivid)_14%,transparent),transparent_60%)] will-change-transform"
    />
  );
}
